import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Full-bleed loading screen shown after the user clicks Begin Chat,
 * while we wait for the first bot reply. Echoes the Ikigai venn motif:
 * four overlapping circles in the brand palette, gently pulsing as they
 * converge on a center point.
 */

const pulse = keyframes`
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.55;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.18);
    opacity: 0.85;
  }
`;

const ellipsis = keyframes`
  0%   { content: ''; }
  25%  { content: '.'; }
  50%  { content: '..'; }
  75%  { content: '...'; }
  100% { content: ''; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgb(55, 65, 81);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 50;
`;

const VennStage = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    width: 180px;
    height: 180px;
    margin-bottom: 32px;
  }
`;

interface BlobProps {
  $color: string;
  $x: number;
  $y: number;
  $delay: number;
}

// 4 overlapping circles arranged in a quincunx around the center,
// each starting its pulse on a staggered delay so they breathe in sequence.
const Blob = styled.div<BlobProps>`
  position: absolute;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  top: calc(50% + ${(p) => p.$y}px);
  left: calc(50% + ${(p) => p.$x}px);
  background: ${(p) => p.$color};
  mix-blend-mode: screen;
  filter: blur(2px);
  animation: ${pulse} 2.4s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}s;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const Caption = styled.p`
  color: #fff;
  font-size: 1.8rem;
  font-weight: 300;
  letter-spacing: 0.02em;
  margin: 0;
  text-align: center;

  /* Animated ellipsis without re-rendering React */
  &::after {
    display: inline-block;
    width: 1.5em;
    text-align: left;
    content: '';
    animation: ${ellipsis} 1.5s steps(4, end) infinite;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const SubCaption = styled.p`
  color: rgba(255, 255, 255, 0.55);
  font-size: 1.3rem;
  font-weight: 300;
  margin: 8px 0 0;
  text-align: center;
  max-width: 320px;
  line-height: 1.4;
`;

export default function LoadingInterview() {
  return (
    <Backdrop role="status" aria-live="polite" aria-label="Loading your interview">
      <VennStage>
        {/* Brand palette: deep purple, magenta, teal, soft amber — same family as the venn diagram */}
        <Blob $color="#600b68" $x={-44} $y={-30} $delay={0} />
        <Blob $color="#a52a8c" $x={44} $y={-30} $delay={0.6} />
        <Blob $color="#5d2e91" $x={-44} $y={30} $delay={1.2} />
        <Blob $color="#c97cc4" $x={44} $y={30} $delay={1.8} />
      </VennStage>
      <Caption>Loading your interview</Caption>
      <SubCaption>Setting up your private coaching session…</SubCaption>
    </Backdrop>
  );
}
